import { STEP_HEIGHT } from './collision.js';

/**
 * The way to somewhere: a GPS line over the building, for the route drawn on
 * the floor and on the minimap (`docs/feel-reference.md`, section 3).
 *
 * The level has no navmesh and its doors, gates, lawns and road are written
 * four different ways in `level.js`; the colliders are one list. So the floor
 * plan is read straight off them as a grid:
 *
 *   floor      each cell's floor is the highest box top under its centre that
 *              a person could stand on (under head height), or the slab
 *   blocked    something rises from a step above that floor to head height at
 *              the centre, widened by a hand's breadth so the line keeps off
 *              the walls. Overhead things (min above head) are not in the way
 *   moving     to a neighbour only within a step's rise of the floor, so a
 *              table is an island and the dock is reached by its stairs; and
 *              a diagonal only where both squares beside it are open, so the
 *              line never cuts a corner through a jamb
 *
 * Then a distance field from the target outward (Dijkstra), so the way from
 * anywhere is downhill and nothing is recomputed as the player walks. The path
 * is pulled straight wherever the grid can see straight.
 *
 * Both the grid and the field are built a fixed number of cells a frame
 * (`pump`), so it never costs a frame anything worth measuring, and a count,
 * not a clock, so a seeded run is the same run. It reads the collision world
 * and decides nothing: it is a reading, like the minimap.
 */

/** Head height for "in the way", metres above a cell's floor. */
const HEAD = 1.7;
/** Box tops above this are walls whatever is under them, not floors. */
const STANDABLE = 1.6;
/**
 * How far the line keeps off a wall, metres: the body's own radius (0.28) and
 * a little, so the line is somewhere a body fits. At 12 cm it ran along the
 * side of the apron stairs close enough that following it put your shoulder on
 * the stairs' edge and stopped you. A three-foot door still has a quarter
 * metre of line through its middle.
 */
const MARGIN = 0.31;
/** How far a target or a start inside something looks for an open cell, in cells. */
const SNAP_CELLS = 12;
/** Arm's reach round a target: every open cell this close is "there", metres. */
const REACH = 1.0;
/** A patch of floor this many cells or fewer is a thing's top, not a room (about 10 m²). */
const ISLAND = 60;
const SQRT2 = Math.SQRT2;

/*
 * `avoid` is rects, in metres as [x0, z0, x1, z1], that no path may enter: for
 * people who must keep off somewhere (a break walk keeps off the main service
 * run, rule 2). The player's own guide passes none.
 */
export function createRouter({ world, bounds, cell = 0.4, margin = MARGIN, avoid = [] }) {
  const x0 = bounds.minX;
  const z0 = bounds.minZ;
  const cols = Math.max(1, Math.ceil((bounds.maxX - bounds.minX) / cell));
  const rows = Math.max(1, Math.ceil((bounds.maxZ - bounds.minZ) / cell));
  const size = cols * rows;
  const floor = new Float32Array(size);
  const blocked = new Uint8Array(size);
  // Float64: a key pushed to the heap is compared with what was stored, and a
  // 32-bit store rounds some of them down, which made live entries look stale.
  const dist = new Float64Array(size);
  let built = 0;
  /** Where the field is from, and how far it has got. */
  let goal = null;
  let goalCell = -1;
  let heap = null;
  let settled = false;
  let generation = 0;

  const indexOf = (cx, cz) => cz * cols + cx;
  const cellOf = (x, z) => {
    const cx = Math.floor((x - x0) / cell);
    const cz = Math.floor((z - z0) / cell);
    if (cx < 0 || cz < 0 || cx >= cols || cz >= rows) return -1;
    return indexOf(cx, cz);
  };
  const centre = (index) => ({
    x: x0 + ((index % cols) + 0.5) * cell,
    z: z0 + (Math.floor(index / cols) + 0.5) * cell,
  });

  function buildCell(index) {
    const { x, z } = centre(index);
    const boxes = world.near(x, z, margin + 0.01);
    let ground = 0;
    for (const box of boxes) {
      if (x < box.min.x || x > box.max.x || z < box.min.z || z > box.max.z) continue;
      if (box.max.y <= STANDABLE && box.max.y > ground) ground = box.max.y;
    }
    let wall = 0;
    for (const box of boxes) {
      if (box.max.y <= ground + STEP_HEIGHT) continue;
      if (box.min.y >= ground + HEAD) continue;
      if (x < box.min.x - margin || x > box.max.x + margin || z < box.min.z - margin || z > box.max.z + margin) continue;
      wall = 1;
      break;
    }
    if (!wall) for (const [ax0, az0, ax1, az1] of avoid) if (x >= ax0 && x <= ax1 && z >= az0 && z <= az1) wall = 1;
    floor[index] = ground;
    blocked[index] = wall;
  }

  /**
   * Whether a cell is within the body's clearance of a drop or a rise bigger
   * than a step: the edge of the dock, the open sides of the apron stairs. The
   * line keeps off those as it keeps off walls, because a body following a
   * line along a stair's open side stands half on it and goes nowhere. Worked
   * out the first time it is asked and kept.
   */
  const ledge = new Int8Array(size).fill(-1);
  const reachCells = Math.max(1, Math.round(margin / cell));
  function nearLedge(index) {
    if (ledge[index] >= 0) return ledge[index] === 1;
    const cx = index % cols;
    const cz = Math.floor(index / cols);
    const here = floor[index];
    let found = 0;
    for (let dz = -reachCells; dz <= reachCells && !found; dz += 1) {
      for (let dx = -reachCells; dx <= reachCells; dx += 1) {
        const nx = cx + dx;
        const nz = cz + dz;
        if (nx < 0 || nz < 0 || nx >= cols || nz >= rows) continue;
        const n = indexOf(nx, nz);
        // Blocked or not: the ground beside a tall step is too near the step
        // to stand on, and it is still a drop from the step.
        if (Math.abs(floor[n] - here) > STEP_HEIGHT) {
          found = 1;
          break;
        }
      }
    }
    ledge[index] = found;
    return found === 1;
  }

  /** Whether a body can step from one cell to a neighbour, clear of walls and drops. */
  const passable = (a, b) => !blocked[b] && Math.abs(floor[a] - floor[b]) <= STEP_HEIGHT && !nearLedge(b);

  /**
   * The nearest open cell to a point, looking outward a little way. With
   * `reached`, the nearest one the field has reached: standing in a pocket
   * between a workbench and the shop wall, the nearest open cell was a scrap
   * of floor connected to nothing, and the line vanished until you stepped out.
   */
  function openNear(x, z, reached = false) {
    const start = cellOf(x, z);
    if (start < 0 || built < size) return -1;
    const ok = (i) => !blocked[i] && (!reached || Number.isFinite(dist[i]));
    if (ok(start)) return start;
    const cx0 = start % cols;
    const cz0 = Math.floor(start / cols);
    for (let r = 1; r <= SNAP_CELLS; r += 1) {
      let best = -1;
      let bestD = Infinity;
      for (let dz = -r; dz <= r; dz += 1) {
        for (let dx = -r; dx <= r; dx += 1) {
          if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
          const cx = cx0 + dx;
          const cz = cz0 + dz;
          if (cx < 0 || cz < 0 || cx >= cols || cz >= rows) continue;
          const i = indexOf(cx, cz);
          if (!ok(i)) continue;
          const d = dx * dx + dz * dz;
          if (d < bestD) {
            bestD = d;
            best = i;
          }
        }
      }
      if (best >= 0) return best;
    }
    return -1;
  }

  /**
   * The field starts from every open cell within arm's reach of the target,
   * whatever floor it is on, and not from one cell snapped to it. A target is
   * usually a thing (a clock, a board, a cot) and the thing is a box: at
   * counter height its top reads as a little raised floor, an island, and a
   * line to the island alone has no way on. So when the cell under the target
   * is on a patch too small to be a room (`ISLAND` cells or fewer, reached
   * without leaving it), the reach is measured from the patch's edges, and the
   * line ends wherever you can stand beside it.
   */
  function startField() {
    dist.fill(Infinity);
    heap = createHeap();
    settled = false;
    goalCell = -1;
    const near = goal && built >= size ? openNear(goal.x, goal.z) : -1;
    if (near < 0) {
      heap = null;
      return;
    }
    // A thing with a size (a locker bank, a rack) is reached from round its
    // edges, not from round its middle, which can be metres inside it.
    const hx = goal.half?.x ?? 0;
    const hz = goal.half?.z ?? 0;
    let box = { minX: goal.x - hx, maxX: goal.x + hx, minZ: goal.z - hz, maxZ: goal.z + hz };
    const patch = patchOf(near);
    if (patch) {
      box = { minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity };
      for (const i of patch) {
        const c = centre(i);
        box.minX = Math.min(box.minX, c.x);
        box.maxX = Math.max(box.maxX, c.x);
        box.minZ = Math.min(box.minZ, c.z);
        box.maxZ = Math.max(box.maxZ, c.z);
      }
    }
    const lo = cellOf(Math.max(x0, box.minX - REACH), Math.max(z0, box.minZ - REACH));
    const hi = cellOf(Math.min(x0 + cols * cell - 1e-6, box.maxX + REACH), Math.min(z0 + rows * cell - 1e-6, box.maxZ + REACH));
    for (let cz = Math.floor(lo / cols); cz <= Math.floor(hi / cols); cz += 1) {
      for (let cx = lo % cols; cx <= hi % cols; cx += 1) {
        const i = indexOf(cx, cz);
        if (blocked[i]) continue;
        const c = centre(i);
        const dx = Math.max(box.minX - c.x, 0, c.x - box.maxX);
        const dz = Math.max(box.minZ - c.z, 0, c.z - box.maxZ);
        if (Math.hypot(dx, dz) > REACH) continue;
        dist[i] = 0;
        heap.push(i, 0);
      }
    }
    goalCell = near;
  }

  /** The cells of a small patch a cell is on, or null when it is part of something bigger. */
  function patchOf(start) {
    const seen = new Set([start]);
    const queue = [start];
    while (queue.length) {
      const at = queue.pop();
      const cx = at % cols;
      const cz = Math.floor(at / cols);
      for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = cx + dx;
        const nz = cz + dz;
        if (nx < 0 || nz < 0 || nx >= cols || nz >= rows) continue;
        const n = indexOf(nx, nz);
        if (seen.has(n) || !passable(at, n)) continue;
        seen.add(n);
        if (seen.size > ISLAND) return null;
        queue.push(n);
      }
    }
    return seen;
  }

  const NEIGHBOURS = [
    [1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1],
    [1, 1, SQRT2], [1, -1, SQRT2], [-1, 1, SQRT2], [-1, -1, SQRT2],
  ];

  function relax(index) {
    const cx = index % cols;
    const cz = Math.floor(index / cols);
    const here = dist[index];
    for (const [dx, dz, cost] of NEIGHBOURS) {
      const nx = cx + dx;
      const nz = cz + dz;
      if (nx < 0 || nz < 0 || nx >= cols || nz >= rows) continue;
      const n = indexOf(nx, nz);
      if (!passable(index, n)) continue;
      if (dx && dz && (!passable(index, indexOf(cx + dx, cz)) || !passable(index, indexOf(cx, cz + dz)))) continue;
      const next = here + cost * cell;
      if (next < dist[n]) {
        dist[n] = next;
        heap.push(n, next);
      }
    }
  }

  /** Whether the grid can see straight from one cell to another, on one floor. */
  function clear(a, b) {
    let cx = a % cols;
    let cz = Math.floor(a / cols);
    const tx = b % cols;
    const tz = Math.floor(b / cols);
    const dx = Math.abs(tx - cx);
    const dz = Math.abs(tz - cz);
    const sx = cx < tx ? 1 : -1;
    const sz = cz < tz ? 1 : -1;
    let err = dx - dz;
    let last = a;
    for (let guard = 0; guard < dx + dz + 2; guard += 1) {
      if (cx === tx && cz === tz) return true;
      const e2 = 2 * err;
      const stepX = e2 > -dz;
      const stepZ = e2 < dx;
      // A diagonal step passes the corner of both cells beside it: both must
      // be open, or the line cuts the corner of a stair or a jamb.
      if (stepX && stepZ) {
        if (!passable(last, indexOf(cx + sx, cz)) || !passable(last, indexOf(cx, cz + sz))) return false;
      }
      if (stepX) {
        err -= dz;
        cx += sx;
      }
      if (stepZ) {
        err += dx;
        cz += sz;
      }
      const i = indexOf(cx, cz);
      if (!passable(last, i)) return false;
      last = i;
    }
    return true;
  }

  return {
    cols,
    rows,
    cell,
    /** The building moved (a barrier opened or closed): read the floor plan again. */
    invalidate() {
      built = 0;
      ledge.fill(-1);
      generation += 1;
      if (goal) startField();
    },

    /**
     * Where the line goes to. A target that moves less than `slack` metres
     * keeps the field it has; further, the field starts again. `half` is a
     * thing's half size on the floor, `{ x, z }`, when the target is a thing.
     */
    target(point, { slack = 1.5, half = null } = {}) {
      if (!point) {
        goal = null;
        heap = null;
        settled = false;
        goalCell = -1;
        return;
      }
      if (goal && Math.hypot(point.x - goal.x, point.z - goal.z) < slack) return;
      goal = { x: point.x, z: point.z, half };
      if (built >= size) startField();
    },

    /** Does up to `budget` cells of work: the grid first, then the field. */
    pump(budget = 6000) {
      let left = budget;
      while (left > 0 && built < size) {
        buildCell(built);
        built += 1;
        left -= 1;
        if (built === size && goal) startField();
      }
      if (built < size || !heap || settled) return;
      while (left > 0) {
        const top = heap.pop();
        if (top === null) {
          settled = true;
          return;
        }
        if (top.key > dist[top.value]) continue;
        relax(top.value);
        left -= 1;
      }
    },

    /** Whether the floor plan is read and the field is complete for this target. */
    get ready() {
      return built >= size && settled;
    },

    /** Changes every time the floor plan is read again. */
    get generation() {
      return generation;
    },

    /**
     * The way from a point to the target: a list of `{ x, y, z }` corners,
     * from here to there, pulled straight, `y` the floor at each. Null while
     * the field is not ready, or when there is no way.
     */
    path(from, { most = 4000 } = {}) {
      if (!goal || !this.ready || goalCell < 0) return null;
      let at = openNear(from.x, from.z, true);
      if (at < 0 || !Number.isFinite(dist[at])) return null;
      const cells = [at];
      for (let guard = 0; guard < most && dist[at] > 0; guard += 1) {
        const cx = at % cols;
        const cz = Math.floor(at / cols);
        let best = at;
        for (const [dx, dz] of NEIGHBOURS) {
          const nx = cx + dx;
          const nz = cz + dz;
          if (nx < 0 || nz < 0 || nx >= cols || nz >= rows) continue;
          const n = indexOf(nx, nz);
          if (dist[n] < dist[best] && passable(at, n)) best = n;
        }
        if (best === at) break;
        at = best;
        cells.push(at);
      }
      // Pulled straight: from each corner, as far along as can be seen.
      const corners = [cells[0]];
      let anchor = 0;
      for (let i = 2; i < cells.length; i += 1) {
        if (!clear(cells[anchor], cells[i])) {
          anchor = i - 1;
          corners.push(cells[anchor]);
        }
      }
      if (cells.length > 1) corners.push(cells[cells.length - 1]);
      const out = corners.map((index) => ({ ...centre(index), y: floor[index] }));
      out[0] = { x: from.x, z: from.z, y: out[0].y };
      // It ends at the thing: its middle, or the near face of something big.
      const last = out[out.length - 1];
      const hx = goal.half?.x ?? 0;
      const hz = goal.half?.z ?? 0;
      out[out.length - 1] = {
        x: Math.min(goal.x + hx, Math.max(goal.x - hx, last.x)),
        z: Math.min(goal.z + hz, Math.max(goal.z - hz, last.z)),
        y: last.y,
      };
      return out;
    },

    /** Metres along the field from a point to the target, or null. */
    distance(from) {
      if (!this.ready) return null;
      const at = openNear(from.x, from.z, true);
      return at >= 0 && Number.isFinite(dist[at]) ? dist[at] : null;
    },

    /** For a check: one cell's reading. */
    probe(x, z) {
      const index = cellOf(x, z);
      if (index < 0 || index >= built) return null;
      return { floor: floor[index], blocked: Boolean(blocked[index]), dist: dist[index] };
    },
  };
}

/** The total length of a path of corners, metres. */
export function pathLength(points) {
  let total = 0;
  for (let i = 1; i < (points?.length ?? 0); i += 1) {
    total += Math.hypot(points[i].x - points[i - 1].x, points[i].z - points[i - 1].z);
  }
  return total;
}

/** A small binary heap of cell indices by distance. */
function createHeap() {
  const keys = [];
  const values = [];
  return {
    push(value, key) {
      keys.push(key);
      values.push(value);
      let i = keys.length - 1;
      while (i > 0) {
        const parent = (i - 1) >> 1;
        if (keys[parent] <= keys[i]) break;
        [keys[parent], keys[i]] = [keys[i], keys[parent]];
        [values[parent], values[i]] = [values[i], values[parent]];
        i = parent;
      }
    },
    pop() {
      if (!keys.length) return null;
      const top = { key: keys[0], value: values[0] };
      const lastKey = keys.pop();
      const lastValue = values.pop();
      if (keys.length) {
        keys[0] = lastKey;
        values[0] = lastValue;
        let i = 0;
        for (;;) {
          const l = 2 * i + 1;
          const r = l + 1;
          let m = i;
          if (l < keys.length && keys[l] < keys[m]) m = l;
          if (r < keys.length && keys[r] < keys[m]) m = r;
          if (m === i) break;
          [keys[m], keys[i]] = [keys[i], keys[m]];
          [values[m], values[i]] = [values[i], values[m]];
          i = m;
        }
      }
      return top;
    },
  };
}
